import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "@/assets/logo.webp";

const navItems = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  { label: "Philosophy", path: "/philosophy" },
  { label: "Our Projects", path: "/projects" },
  { label: "Services", path: "/services" },
  { label: "Our Blog", path: "/blog" },
  { label: "Contact Us", path: "/contact" },
];

interface NavbarProps {
  transparent?: boolean;
}

const Navbar = ({ transparent = false }: NavbarProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!transparent) return;
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparent]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const isTransparent = transparent && !scrolled && !mobileOpen && !isMobile;

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? "bg-transparent border-b border-transparent"
          : "bg-background/95 backdrop-blur-md border-b border-border"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-1 sm:gap-1.5">
            <motion.img
              src={logo}
              alt="Dhivisha Architects"
              className={`h-14 w-auto transition-all duration-300 ${
                isTransparent
                  ? "drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)]"
                  : "drop-shadow-[0_2px_8px_hsl(40,50%,48%,0.3)]"
              }`}
              whileHover={{ scale: 1.05, rotateY: 10 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.3 }}
              style={{ transformStyle: "preserve-3d" }}
            />
            <div className="leading-none">
              <span
                className={`text-sm sm:text-lg font-bold block uppercase tracking-[0.15em] body-sans transition-colors duration-300 leading-[1.1] ${
                  isTransparent
                    ? "text-foreground sm:text-white drop-shadow-none sm:drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)]"
                    : "text-foreground"
                }`}
              >
                DHIVISHA
              </span>
              <span
                className={`text-[8px] sm:text-[10px] uppercase tracking-[0.25em] font-medium transition-colors duration-300 leading-none ${
                  isTransparent
                    ? "text-primary sm:text-white/70"
                    : "text-primary"
                }`}
              >
                ARCHITECTS
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 text-sm font-medium tracking-wide uppercase transition-colors duration-200 ${
                  location.pathname === item.path
                    ? isTransparent
                      ? "text-white border-b-2 border-white"
                      : "text-primary border-b-2 border-primary"
                    : isTransparent
                    ? "text-white/80 hover:text-white"
                    : "text-foreground/70 hover:text-primary"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Mobile toggle */}
          <button
            className={`lg:hidden p-2 transition-colors ${
              isTransparent ? "text-foreground sm:text-white" : "text-foreground"
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background border-b border-border overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              {navItems.map((item, i) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.2 }}
                >
                  <Link
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`block px-4 py-3 text-sm font-medium uppercase tracking-wide transition-colors active:scale-[0.98] ${
                      location.pathname === item.path
                        ? "text-primary bg-primary/10"
                        : "text-foreground/70 hover:text-primary hover:bg-muted"
                    }`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
